import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { languages, postMessage } from "../utils/helper";

const LocaleSelection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [selected, setSelected] = useState("en-IN");

  const handleContinue = () => {
    try {
      const api_key = state?.api_key;
      const result = state?.result;
      const moduleState = state?.moduleState || {};
      const autoSubmissionTimeout = state?.autoSubmissionTimeout;
      const presignedUrl = state?.presignedUrl;
      const baseImageUploaded = state?.baseImageUploaded ?? false;

      if (moduleState.shouldDoFaceMatch && !baseImageUploaded) {
        if (!presignedUrl) throw new Error("Presigned URL for face match image not found in workflow.");
        navigate("/upload-documents", {
          replace: true,
          state: { api_key, result, moduleState, locale: selected, autoSubmissionTimeout, presignedUrl },
        });
      } else if (moduleState.shouldDoVitals)
        navigate("/scan-instructions", {
          state: { api_key, result, moduleState, flowType: "FULL_SCAN", locale: selected, autoSubmissionTimeout },
          replace: true,
        });
      else if (moduleState.shouldDoPIVC)
        navigate("/scan-instructions", {
          state: { api_key, result, moduleState, flowType: "VIDEO_ONLY", locale: selected, autoSubmissionTimeout },
          replace: true,
        });
      else throw new Error("Unable to determine next step in workflow based on current status.");
    } catch (error) {
      console.error("Error checking workflow status:", error);
      postMessage("error", error?.message || "Failed to initialize workflow");
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-primary text-white" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <div className="px-6 pt-12 pb-6">
        <h1 className="text-2xl font-bold text-white">Select Language</h1>
        <p className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>
          Choose the language for your consent text
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="grid grid-cols-2 gap-3">
          {languages.map((lang) => {
            const isSelected = selected === lang.locale;
            return (
              <button
                key={lang.locale}
                type="button"
                onClick={() => setSelected(lang.locale)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left w-full"
                style={{
                  background: isSelected ? "linear-gradient(135deg, #4f46e5, #7c3aed)" : "rgba(255,255,255,0.05)",
                  border: isSelected ? "1.5px solid rgba(139,92,246,0.7)" : "1.5px solid rgba(255,255,255,0.07)",
                  boxShadow: isSelected ? "0 4px 20px rgba(99,102,241,0.3)" : "none",
                  transform: isSelected ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.18s ease",
                }}>
                <span
                  className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{
                    background: isSelected ? "#fff" : "rgba(255,255,255,0.15)",
                    border: isSelected ? "none" : "1.5px solid rgba(255,255,255,0.25)",
                  }}>
                  {isSelected && <span className="w-2 h-2 rounded-full" style={{ background: "#6366f1" }} />}
                </span>

                <span className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold truncate" style={{ color: isSelected ? "#fff" : "rgba(255,255,255,0.85)" }}>
                    {lang.native}
                  </span>
                  <span
                    className="text-xs truncate"
                    style={{
                      color: isSelected ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                    }}>
                    {lang.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="shrink-0 px-6 pb-10 pt-4"
        style={{
          background: "linear-gradient(to top, var(--color-primary, #0a0f2a) 70%, transparent)",
        }}>
        <button
          type="button"
          onClick={handleContinue}
          className="w-full py-3.5 rounded-full text-white font-semibold text-base"
          style={{
            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
            boxShadow: "0 6px 24px rgba(99,102,241,0.4)",
            letterSpacing: "0.01em",
          }}>
          Continue
        </button>
      </div>
    </div>
  );
};

export default LocaleSelection;
